import React from 'react';
import {
    Box,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    SimpleGrid,
    Heading,
    Text,
    Badge,
    HStack,
    VStack,
    Divider,
    useColorModeValue
} from '@chakra-ui/react';
import { ComparisonItem } from '../types';
import { ImageCarousel } from './ImageCarousel';
import { ImageWithBoxes } from './ImageWithBoxes';
import { formatQty, formatDiscrepancy, formatIndonesianDateTime } from '../utils';

interface ComparisonCardProps {
    item: ComparisonItem;
    comparisonType: ComparisonType;
}

export type ComparisonType =
    | 'JEJAK_SAKTI'
    | 'JEJAK_AI'
    | 'SAKTI_AI';

export const ComparisonCard: React.FC<ComparisonCardProps> = ({ item, comparisonType }) => {
    const { pemanen_name, pemanen_nip, block, tph, estate, divisi, mutu_buah, sakti_bcc, discrepancy, ai_bunch_count } = item;

    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const labelColor = useColorModeValue('gray.500', 'gray.400');
    const cardBg = useColorModeValue('white', 'gray.800');
    const highlightBg = useColorModeValue('red.50', 'rgba(229, 62, 62, 0.15)');
    const highlightText = useColorModeValue('red.600', 'red.300');
    
    // Check if there is any difference between Jejak and Sakti
    const hasDiff = Object.values(discrepancy).some(val => val !== 0);

    const comparisonTypeLabel: Record<ComparisonType, string> = {
        JEJAK_SAKTI: 'JEJAK ↔ SAKTI',
        JEJAK_AI: 'JEJAK ↔ AI',
        SAKTI_AI: 'SAKTI ↔ AI',
    };

    return (
        <Card 
            bg={cardBg}
            border="1px solid" 
            borderColor={borderColor} 
            borderRadius="lg" 
            overflow="hidden" 
            boxShadow="sm"
            mb={4}
        >
            {/* Header: Harvester Info and Block/TPH */}
            <CardHeader bg={useColorModeValue('gray.50', 'gray.900')} py={3} px={5}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} alignItems="center">
                    <VStack align="start" spacing={0.5}>
                        <Text fontSize="xs" fontWeight="bold" color={labelColor}>PEMANEN</Text>
                        <Text fontWeight="bold" fontSize="md" lineHeight="short">{pemanen_name || 'N/A'} ({pemanen_nip || 'N/A'})</Text>
                        {sakti_bcc.harvest_nbr && (
                            <Text fontSize="xs" color="gray.400" fontWeight="medium">
                                {sakti_bcc.harvest_nbr}
                            </Text>
                        )}
                    </VStack>
                    <VStack align={{ base: 'start', md: 'end' }} spacing={1} mt={{ base: 2, md: 0 }} w="full">
                        <HStack spacing={2} align="center">
                            <Text
                                fontSize="xs"
                                fontWeight="bold"
                                color={labelColor}
                            >
                                {comparisonTypeLabel[comparisonType]} :
                            </Text>

                            <Badge
                                colorScheme={hasDiff ? 'red' : 'green'}
                                variant="subtle"
                                fontSize="xs"
                                px={2.5}
                                py={0.5}
                                borderRadius="md"
                                fontWeight="bold"
                                textTransform="uppercase"
                                letterSpacing="wider"
                            >
                                {hasDiff ? 'Ada Selisih' : 'Tidak Ada Selisih'}
                            </Badge>
                        </HStack>
                        <HStack spacing={4} fontSize="sm" flexWrap="wrap" justify={{ base: 'start', md: 'end' }}>
                            <Box>
                                <Text as="span" color={labelColor} fontWeight="bold">ESTATE: </Text>
                                <Text as="span" fontWeight="semibold" color={useColorModeValue('black', 'white')}>{estate || 'N/A'} ({divisi || 'N/A'})</Text>
                            </Box>
                            <Box>
                                <Text as="span" color={labelColor} fontWeight="bold">BLOK: </Text>
                                <Text as="span" fontWeight="semibold" color={useColorModeValue('black', 'white')}>{block || 'N/A'}</Text>
                            </Box>
                            <Box>
                                <Text as="span" color={labelColor} fontWeight="bold">TPH: </Text>
                                <Text as="span" fontWeight="semibold" color={useColorModeValue('black', 'white')}>{tph || 'N/A'}</Text>
                            </Box>
                        </HStack>
                    </VStack>
                </SimpleGrid>
            </CardHeader>

            <CardBody px={5} py={4}>
                {/* 2-Column Grid: Left (Jejak / Mutu Buah), Right (Sakti BCC) */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    
                    {/* LEFT COLUMN: MUTU BUAH (JEJAK) */}
                    <VStack align="stretch" spacing={3}>
                        <VStack align="start" spacing={0.5}>
                            <Text fontWeight="bold" fontSize="sm" lineHeight="short">Jejak (Mutu Buah)</Text>
                            <HStack spacing={1.5} fontSize="xs" color="gray.400" flexWrap="wrap" rowGap={1}>
                                <Text fontWeight="medium">{formatIndonesianDateTime(mutu_buah.inspection_datetime)}</Text>
                                <Text color="gray.300">•</Text>
                                <Text color={labelColor} fontWeight="medium">
                                    {mutu_buah.created_by_name || '-'} ({mutu_buah.job_level || '-'})
                                </Text>
                            </HStack>
                        </VStack>
                        
                        <ImageCarousel pictures={mutu_buah.pictures} placeholderText="Tidak ada foto inspeksi" />

                        <VStack align="stretch" spacing={1.5} fontSize="sm" divider={<Divider borderColor={useColorModeValue('gray.100', 'gray.700')} />}>
                            <HStack justify="space-between" bg={discrepancy.bunch_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Janjang </Text>
                                <Text fontWeight="bold" color={discrepancy.bunch_qty !== 0 ? highlightText : 'inherit'}>{formatQty(mutu_buah.jejak_bunch_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.ripe_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Masak </Text>
                                <Text fontWeight="bold" color={discrepancy.ripe_qty !== 0 ? highlightText : 'inherit'}>{formatQty(mutu_buah.jejak_ripe_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.unripe_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Mentah </Text>
                                <Text fontWeight="bold" color={discrepancy.unripe_qty !== 0 ? highlightText : 'inherit'}>{formatQty(mutu_buah.jejak_unripe_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.rotten_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Busuk </Text>
                                <Text fontWeight="bold" color={discrepancy.rotten_qty !== 0 ? highlightText : 'inherit'}>{formatQty(mutu_buah.jejak_rotten_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.loose_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Brondolan (kg)</Text>
                                <Text fontWeight="bold" color={discrepancy.loose_qty !== 0 ? highlightText : 'inherit'}>
                                    {formatQty(mutu_buah.jejak_loose_qty) !== '-' ? `${mutu_buah.jejak_loose_qty} kg` : '-'}
                                </Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_01_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>TBS {"<"} 5 kg</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_01_qty !== 0 ? highlightText : 'inherit'}>{formatQty(mutu_buah.jejak_abnormal_01_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_02_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Dimakan tikus</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_02_qty !== 0 ? highlightText : 'inherit'}>{formatQty(mutu_buah.jejak_abnormal_02_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_03_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Buah batu</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_03_qty !== 0 ? highlightText : 'inherit'}>{formatQty(mutu_buah.jejak_abnormal_03_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_04_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Parthenocarpy</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_04_qty !== 0 ? highlightText : 'inherit'}>{formatQty(mutu_buah.jejak_abnormal_04_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_05_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Tangkai panjang</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_05_qty !== 0 ? highlightText : 'inherit'}>{formatQty(mutu_buah.jejak_abnormal_05_qty)}</Text>
                            </HStack>
                        </VStack>
                    </VStack>

                    {/* RIGHT COLUMN: SAKTI BCC */}
                    <VStack align="stretch" spacing={3}>
                        <VStack align="start" spacing={0.5}>
                            <Text fontWeight="bold" fontSize="sm" lineHeight="short">Sakti (BCC)</Text>
                            <HStack spacing={1.5} fontSize="xs" color="gray.400" flexWrap="wrap" rowGap={1}>
                                <Text fontWeight="medium">{formatIndonesianDateTime(sakti_bcc.harvest_datetime)}</Text>
                                <Text color="gray.300">•</Text>
                                <Text color={labelColor} fontWeight="medium">
                                    {sakti_bcc.created_by || '-'} ({sakti_bcc.job_level || '-'})
                                </Text>
                            </HStack>
                        </VStack>

                        <ImageCarousel pictures={sakti_bcc.pictures} placeholderText="Tidak ada foto timbangan" />

                        <VStack align="stretch" spacing={1.5} fontSize="sm" divider={<Divider borderColor={useColorModeValue('gray.100', 'gray.700')} />}>
                            <HStack justify="space-between" bg={discrepancy.bunch_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Janjang </Text>
                                <Text fontWeight="bold" color={discrepancy.bunch_qty !== 0 ? highlightText : 'inherit'}>{formatQty(sakti_bcc.sakti_bunch_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.ripe_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Masak </Text>
                                <Text fontWeight="bold" color={discrepancy.ripe_qty !== 0 ? highlightText : 'inherit'}>{formatQty(sakti_bcc.sakti_ripe_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.unripe_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Mentah </Text>
                                <Text fontWeight="bold" color={discrepancy.unripe_qty !== 0 ? highlightText : 'inherit'}>{formatQty(sakti_bcc.sakti_unripe_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.rotten_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Busuk </Text>
                                <Text fontWeight="bold" color={discrepancy.rotten_qty !== 0 ? highlightText : 'inherit'}>{formatQty(sakti_bcc.sakti_rotten_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.loose_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Brondolan (kg)</Text>
                                <Text fontWeight="bold" color={discrepancy.loose_qty !== 0 ? highlightText : 'inherit'}>
                                    {formatQty(sakti_bcc.sakti_loose_qty) !== '-' ? `${sakti_bcc.sakti_loose_qty} kg` : '-'}
                                </Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_01_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>TBS {"<"} 5 kg</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_01_qty !== 0 ? highlightText : 'inherit'}>{formatQty(sakti_bcc.sakti_abnormal_01_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_02_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Dimakan tikus</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_02_qty !== 0 ? highlightText : 'inherit'}>{formatQty(sakti_bcc.sakti_abnormal_02_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_03_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Buah batu</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_03_qty !== 0 ? highlightText : 'inherit'}>{formatQty(sakti_bcc.sakti_abnormal_03_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_04_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Parthenocarpy</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_04_qty !== 0 ? highlightText : 'inherit'}>{formatQty(sakti_bcc.sakti_abnormal_04_qty)}</Text>
                            </HStack>
                            <HStack justify="space-between" bg={discrepancy.abnormal_05_qty !== 0 ? highlightBg : 'transparent'} px={2} py={0.5} borderRadius="md">
                                <Text color={useColorModeValue('gray.600', 'gray.300')}>Tangkai panjang</Text>
                                <Text fontWeight="bold" color={discrepancy.abnormal_05_qty !== 0 ? highlightText : 'inherit'}>{formatQty(sakti_bcc.sakti_abnormal_05_qty)}</Text>
                            </HStack>
                        </VStack>
                    </VStack>

                    <VStack align="stretch" spacing={3}>
                        <VStack align="start" spacing={0.5}>
                            <Text
                                fontWeight="bold"
                                fontSize="sm"
                                lineHeight="short"
                            >
                                AI Bunch Counting
                            </Text>

                            <HStack
                                spacing={1.5}
                                fontSize="xs"
                                color="gray.400"
                                flexWrap="wrap"
                                rowGap={1}
                            >
                                <Text fontWeight="medium">
                                    {formatIndonesianDateTime(ai_bunch_count.counted_at)}
                                </Text>

                                {/* <Text color="gray.300">•</Text>

                                <Text
                                    color={ai_bunch_count.is_flagged ? "orange.400" : "green.400"}
                                    fontWeight="medium"
                                >
                                    {ai_bunch_count.status}
                                </Text> */}

                                {/* <Text color="gray.300">•</Text>

                                <Text color={labelColor} fontWeight="medium">
                                    Model {ai_bunch_count.model_version}
                                </Text> */}
                            </HStack>
                        </VStack>

                        {/* <ImageCarousel pictures={ai_bunch_count.pictures} placeholderText="Tidak ada foto timbangan" /> */}
                        <ImageWithBoxes image={ai_bunch_count.image_url} boxes={ai_bunch_count.boxes} />
                        {/* <ImageCarousel
                            pictures={
                                item.ai_bunch_count?.image_url
                                    ? [item.ai_bunch_count.image_url]
                                    : []
                            } placeholderText="Tidak ada foto timbangan"
                        /> */}

                        <VStack
                            align="stretch"
                            spacing={1.5}
                            fontSize="sm"
                            divider={
                                <Divider
                                    borderColor={useColorModeValue("gray.100", "gray.700")}
                                />
                            }
                        >
                            {/* Jumlah bunch hasil AI */}
                            <HStack
                                justify="space-between"
                                bg={
                                    ai_bunch_count.delta_vs_sakti !== 0
                                        ? highlightBg
                                        : "transparent"
                                }
                                px={2}
                                py={0.5}
                                borderRadius="md"
                            >
                                <Text
                                    color={useColorModeValue("gray.600", "gray.300")}
                                >
                                    Janjang
                                </Text>

                                <Text
                                    fontWeight="bold"
                                    color={
                                        ai_bunch_count.delta_vs_sakti !== 0
                                            ? highlightText
                                            : "inherit"
                                    }
                                >
                                    {formatQty(ai_bunch_count.bunch_qty)}
                                </Text>
                            </HStack>

                            {/* Delta vs Sakti */}
                            <HStack
                                justify="space-between"
                                bg={
                                    ai_bunch_count.delta_vs_sakti !== 0
                                        ? highlightBg
                                        : "transparent"
                                }
                                px={2}
                                py={0.5}
                                borderRadius="md"
                            >
                                <Text
                                    color={useColorModeValue("gray.600", "gray.300")}
                                >
                                    Selisih vs Sakti
                                </Text>

                                <Text
                                    fontWeight="bold"
                                    color={
                                        ai_bunch_count.delta_vs_sakti !== 0
                                            ? highlightText
                                            : "inherit"
                                    }
                                >
                                    {ai_bunch_count.delta_vs_sakti > 0 ? "+" : ""}
                                    {formatQty(ai_bunch_count.delta_vs_sakti)}
                                </Text>
                            </HStack>

                            {/* Total detection */}
                            {/* <HStack justify="space-between" px={2} py={0.5}>
                                <Text
                                    color={useColorModeValue("gray.600", "gray.300")}
                                >
                                    Total Detection
                                </Text>

                                <Text fontWeight="bold">
                                    {ai_bunch_count.boxes?.length ?? 0}
                                </Text>
                            </HStack> */}
                        </VStack>
                    </VStack>


                </SimpleGrid>
            </CardBody>

            <Divider />

            {/* Footer: Discrepancy details */}
            <CardFooter bg={useColorModeValue('gray.50', 'gray.900')} py={2.5} px={5}>
                <HStack wrap="wrap" spacing={3} fontSize="xs" width="100%">
                    <Text fontWeight="extrabold" color={labelColor} mr={2}>SELISIH:</Text>
                    
                    {!hasDiff ? (
                        <Text fontWeight="bold" color="green.500">Cocok (Tidak ada selisih data)</Text>
                    ) : (
                        <>
                            {discrepancy.bunch_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    Janjang: {formatDiscrepancy(discrepancy.bunch_qty)}
                                </Badge>
                            )}
                            {discrepancy.ripe_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    Matang: {formatDiscrepancy(discrepancy.ripe_qty)}
                                </Badge>
                            )}
                            {discrepancy.unripe_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    Mentah: {formatDiscrepancy(discrepancy.unripe_qty)}
                                </Badge>
                            )}
                            {discrepancy.rotten_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    Busuk: {formatDiscrepancy(discrepancy.rotten_qty)}
                                </Badge>
                            )}
                            {discrepancy.loose_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    Brondolan: {formatDiscrepancy(discrepancy.loose_qty) !== '-' ? `${formatDiscrepancy(discrepancy.loose_qty)} kg` : '-'}
                                </Badge>
                            )}
                            {discrepancy.abnormal_01_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    TBS {"<"} 5 kg: {formatDiscrepancy(discrepancy.abnormal_01_qty)}
                                </Badge>
                            )}
                            {discrepancy.abnormal_02_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    Dimakan tikus: {formatDiscrepancy(discrepancy.abnormal_02_qty)}
                                </Badge>
                            )}
                            {discrepancy.abnormal_03_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    Buah batu: {formatDiscrepancy(discrepancy.abnormal_03_qty)}
                                </Badge>
                            )}
                            {discrepancy.abnormal_04_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    Parthenocarpy: {formatDiscrepancy(discrepancy.abnormal_04_qty)}
                                </Badge>
                            )}
                            {discrepancy.abnormal_05_qty !== 0 && (
                                <Badge colorScheme="red" px={2} py={0.5} borderRadius="sm">
                                    Tangkai panjang: {formatDiscrepancy(discrepancy.abnormal_05_qty)}
                                </Badge>
                            )}
                        </>
                    )}
                </HStack>
            </CardFooter>
        </Card>
    );
};
